/* eslint-disable @typescript-eslint/dot-notation */
import { DecimalPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit } from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';
import { NbDialogService } from '@nebular/theme';
import { Actions, ofActionCompleted, Store } from '@ngxs/store';
import { Observable, Subject, takeUntil } from 'rxjs';
import { OktaRestriction } from 'src/app/core/services/okta-restrictions.service';
import { UtilityService } from 'src/app/core/services/utility.service';
import { RouterState } from 'src/app/features/auth/redux/router-state';
import { SourceImage } from 'src/app/models/drb-models/source-image';
import { ACTIONS, ENVIRONMENTS } from 'src/app/shared/constants/general-constants';
import { NgxGenericModalComponent } from 'src/app/shared/ui/ngx-generic-modal/ngx-generic-modal.component';
import { drbMetadata } from 'src/app/shared/utils/drb-metadata';
import { ENVIRONMENT } from 'src/environments/environment';
import { DeleteDrbComponent } from '../../delete-drb/delete-drb.component';
import { DrbState, LoadDrb, LoadDrbByVersionSuccess } from '../../redux';
import { EditImageComponent } from './edit-image/edit-image.component';
import { ViewImageComponent } from './view-image/view-image.component';
import { SharedModule } from '../../../../shared/shared.module';

@Component({
    selector: 'app-source-images',
    changeDetection: ChangeDetectionStrategy.Default,
    templateUrl: './source-images.component.html',
    styleUrls: ['./source-images.component.css'],
    providers: [DecimalPipe],
    imports: [SharedModule, RouterOutlet]
})
export class SourceImagesComponent implements OnInit, OnDestroy {
  public drb$: Observable<any> = inject(Store).select(DrbState.drb);
  public image$: Observable<any[]> = inject(Store).select(DrbState.sourceImages);
  public viewImage$: Observable<any[]> = inject(Store).select(DrbState.view_sourceImages);
  public username$: Observable<string> = inject(Store).select(RouterState.username);
  private unsubscribe: Subject<void> = new Subject();

  public tableData;
  public showActions = [];
  public description;
  public metaData;
  public username;
  public type = 'image-source';
  public bundle_name: string;

  constructor(
    private router: Router,
    private store: Store,
    private action$: Actions,
    private dialogService: NbDialogService,
    public utility: UtilityService,
    private restrictionService: OktaRestriction
  ) {}

  public ngOnInit() {
    this.bundle_name = this.router.url.split('/')[2];
    if (this.router.url.endsWith('create/images')) {
      this.description = $localize`View, add and edit image data`;
      this.image$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => (this.tableData = data));
      this.drb$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => (this.metaData = drbMetadata(data)));
      this.username$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => (this.username = data));
      this.checkOwnerOrContributor();
    } else if (this.router.url.endsWith('view/images')) {
      this.description = $localize`View image data`;
      this.viewImage$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => (this.tableData = data));
      this.action$.pipe(ofActionCompleted(LoadDrbByVersionSuccess)).subscribe((data) => {
        if (data) {
          this.viewImage$.pipe(takeUntil(this.unsubscribe)).subscribe((loadedData) => (this.tableData = loadedData));
        }
      });
    }
  }

  public ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  public clickedButton($event) {
    if ($event.action === ACTIONS.REFRESH) {
      this.dialogService
        .open(NgxGenericModalComponent, {
          context: {
            title: $localize`Confirm Refresh`,
            message: $localize`Are you sure that you want to refresh draft version?`
          }
        })
        .onClose.subscribe((data) => {
          if (data) {
            this.tableData = undefined;
            this.store.dispatch(new LoadDrb(this.bundle_name));
          }
        });
    }
    if ($event.action === ACTIONS.CREATE) {
      this.dialogService.open(EditImageComponent, {
        closeOnEsc: false,
        closeOnBackdropClick: false,
        context: {
          action: $event.action,
          image: new SourceImage(),
          bundle_name: this.bundle_name
        }
      });
    }
    if ($event.action === ACTIONS.VIEW) {
      const image: SourceImage = $event.data;
      this.dialogService.open(ViewImageComponent, {
        closeOnEsc: false,
        closeOnBackdropClick: false,
        context: {
          image: JSON.parse(JSON.stringify(image))
        }
      });
    }
    if ($event.action === ACTIONS.EDIT) {
      const image: SourceImage = $event.data;
      this.dialogService.open(EditImageComponent, {
        closeOnEsc: false,
        closeOnBackdropClick: false,
        context: {
          action: $event.action,
          image: JSON.parse(JSON.stringify(image)),
          bundle_name: this.bundle_name
        }
      });
    }
    if ($event.action === ACTIONS.DELETE) {
      const image: SourceImage = $event.data;
      this.dialogService.open(DeleteDrbComponent, {
        closeOnEsc: false,
        closeOnBackdropClick: false,
        context: {
          item: JSON.parse(JSON.stringify(image)),
          type: this.type,
          bundle_name: this.bundle_name
        }
      });
    }
  }

  public checkOwnerOrContributor() {
    if (this.restrictionService.manager_permission) {
      if (this.username === this.metaData.owner || this.metaData.contributors.includes(this.username)) {
        this.showActions = [ACTIONS.CREATE, ACTIONS.EDIT];
        if (ENVIRONMENT.name === ENVIRONMENTS.STAGE) {
          this.showActions.push(ACTIONS.DELETE);
        }
      }
    }
  }
}
